import React from "react";
import { fieldCls } from "components/InlineTable";
import { KraArea, KraObjective } from "services/Feedback/feedbackInterface";

/**
 * The KRA area and objective pickers for a goal row.
 *
 * NOTE: extracted because both goal editors need them and only one had them
 * right. The contract requires that a row whose area or objective has SINCE
 * been deactivated still renders its saved name and sends the same id back —
 * the goal-sheet screen did that, the manager form did not, so an approving
 * reviewer saw an empty, required-looking select where the employee had picked
 * a since-retired objective. One component, one behaviour, both callers.
 *
 * Styling is `fieldCls` from components/InlineTable — the app's shared field
 * appearance — so these introduce no new input style.
 */

interface SelectShellProps {
  id?: string;
  ariaLabel?: string;
  disabled?: boolean;
  hasError?: boolean;
}

export interface KraAreaSelectProps extends SelectShellProps {
  value: number | null;
  areas: KraArea[];
  /** `areaName` copied onto the saved row; shown when the area is gone. */
  savedName?: string;
  onChange: (areaId: number | null) => void;
}

export const KraAreaSelect: React.FC<KraAreaSelectProps> = ({
  id,
  ariaLabel,
  disabled = false,
  hasError = false,
  value,
  areas,
  savedName,
  onChange,
}) => {
  // Absent from the list = deactivated since this row was saved.
  const missing = value !== null && !areas.some((area) => area.id === value);

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value ?? ""}
      disabled={disabled}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className={fieldCls(hasError)}
    >
      <option value="">Select area</option>
      {missing && (
        <option value={value as number}>{savedName ?? "Inactive area"} (inactive)</option>
      )}
      {areas.map((area) => (
        <option key={area.id} value={area.id}>
          {area.name}
        </option>
      ))}
    </select>
  );
};

export interface KraObjectiveSelectProps extends SelectShellProps {
  value: number | null;
  /** Already narrowed to the chosen area and to objectives free on this sheet. */
  objectives: KraObjective[];
  /** False disables the control and explains why in the placeholder. */
  areaChosen: boolean;
  /** `objectiveName` copied onto the saved row; shown when the objective is gone. */
  savedName?: string;
  onChange: (objectiveId: number | null) => void;
}

export const KraObjectiveSelect: React.FC<KraObjectiveSelectProps> = ({
  id,
  ariaLabel,
  disabled = false,
  hasError = false,
  value,
  objectives,
  areaChosen,
  savedName,
  onChange,
}) => {
  const missing = value !== null && !objectives.some((objective) => objective.id === value);

  return (
    <select
      id={id}
      aria-label={ariaLabel}
      value={value ?? ""}
      disabled={disabled || !areaChosen}
      onChange={(e) => onChange(e.target.value === "" ? null : Number(e.target.value))}
      className={fieldCls(hasError)}
    >
      <option value="">{areaChosen ? "Select objective" : "Pick an area first"}</option>
      {missing && (
        <option value={value as number}>{savedName ?? "Inactive objective"} (inactive)</option>
      )}
      {objectives.map((objective) => (
        <option key={objective.id} value={objective.id}>
          {objective.name}
        </option>
      ))}
    </select>
  );
};
